/**
 * Credential Modal Component
 * 
 * Modal for collecting platform credentials before extraction.
 * Matches the theme and style of PlatformCredentialsModal.
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, KeyIcon, LockClosedIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { CredentialFields, CredentialData } from '../../types/userInteraction';

interface CredentialModalProps {
  isOpen: boolean;
  platformName: string;
  credentialFields: CredentialFields;
  onSubmit: (credentials: CredentialData) => void;
  onCancel: () => void;
}

export const CredentialModal: React.FC<CredentialModalProps> = ({
  isOpen,
  platformName,
  credentialFields,
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState<CredentialData>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({});
      setErrors({});
      
      // Focus first input after modal opens
      setTimeout(() => {
        const firstInput = document.querySelector<HTMLInputElement>(
          '#credential-modal input'
        );
        firstInput?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Handle body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleChange = (fieldName: string, value: string) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    // Clear error for this field
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    Object.entries(credentialFields).forEach(([fieldName, fieldConfig]) => {
      if (fieldConfig.required && !formData[fieldName]?.trim()) {
        newErrors[fieldName] = `${fieldConfig.label} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validate()) {
      onSubmit(formData);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  if (!isOpen) return null;

  // Sort fields to ensure username comes before password
  const sortedFields = Object.entries(credentialFields).sort(([keyA], [keyB]) => {
    // Username-like fields first
    if (keyA.toLowerCase().includes('username') || keyA.toLowerCase().includes('email')) return -1;
    if (keyB.toLowerCase().includes('username') || keyB.toLowerCase().includes('email')) return 1;
    // Password-like fields last
    if (keyA.toLowerCase().includes('password')) return 1;
    if (keyB.toLowerCase().includes('password')) return -1;
    return 0;
  });

  return createPortal(
    <div 
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onCancel}
      onKeyDown={handleKeyDown}
    >
      <div 
        className="relative bg-theme-bg dark:bg-dark-theme-bg rounded-lg shadow-2xl w-full max-w-md mx-4 overflow-hidden border border-theme-border dark:border-dark-theme-border"
        onClick={(e) => e.stopPropagation()}
        id="credential-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-theme-border dark:border-dark-theme-border">
          <div className="flex items-center space-x-3">
            <KeyIcon className="w-6 h-6 text-theme-text-secondary dark:text-dark-theme-text-secondary" />
            <h3 className="text-xl font-semibold text-theme-text dark:text-dark-theme-text">
              Platform Credentials Required
            </h3>
          </div>
          <button
            onClick={onCancel}
            className="text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text transition-colors"
            aria-label="Close"
            title="Close credentials modal"
          >
            <XMarkIcon className="w-6 h-6" aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Enter credentials for <span className="font-semibold">{platformName}</span>.
              They will be securely stored for your organization.
            </p>
          </div>

          {/* Dynamic Fields */}
          <div className="space-y-4">
            {sortedFields.map(([fieldName, fieldConfig]) => (
              <div key={fieldName}>
                <label 
                  htmlFor={`cred_${fieldName}`}
                  className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-2"
                >
                  {fieldConfig.label}
                  {fieldConfig.required && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
                <input
                  type={fieldConfig.type}
                  id={`cred_${fieldName}`}
                  name={fieldName}
                  value={formData[fieldName] || ''}
                  onChange={(e) => handleChange(fieldName, e.target.value)}
                  placeholder={fieldConfig.placeholder}
                  required={fieldConfig.required}
                  className={`w-full px-4 py-2 rounded-md border bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:border-transparent ${
                    errors[fieldName] 
                      ? 'border-red-500' 
                      : 'border-theme-border dark:border-dark-theme-border'
                  }`}
                  autoComplete={fieldConfig.type === 'password' ? 'current-password' : 'off'}
                />
                {errors[fieldName] && (
                  <div className="flex items-center mt-2 space-x-1">
                    <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />
                    <p className="text-sm text-red-500">
                      {errors[fieldName]}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 font-medium text-sm rounded-md bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary text-theme-text dark:text-dark-theme-text hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center px-6 py-2 space-x-2 text-white rounded-md transition-opacity duration-150 bg-primary-light dark:bg-primary-dark hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light dark:focus:ring-primary-dark"
            >
              <LockClosedIcon className="w-4 h-4" />
              <span>Save Credentials</span>
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};
